import { Link } from "wouter";
import { useListMembers, useListSquads } from "@workspace/api-client-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useState } from "react";

type SortKey = "streak" | "adherence" | "name";

export default function Members() {
  const { data: members, isLoading, isError } = useListMembers();
  const { data: squads } = useListSquads();
  const [sortBy, setSortBy] = useState<SortKey>("streak");

  const sortedMembers = [...(members ?? [])].sort((a, b) => {
    if (sortBy === "streak") return b.currentStreak - a.currentStreak;
    if (sortBy === "adherence") return b.adherencePct - a.adherencePct;
    return a.name.localeCompare(b.name);
  });

  const squadMap = Object.fromEntries((squads ?? []).map((s) => [s.id, s.name]));

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="text-destructive font-mono text-sm font-medium">Unable to load runners</div>
        <div className="text-muted-foreground text-xs text-center max-w-xs">
          There was a problem fetching your data. Check your connection or try refreshing the page.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold mb-1">Runners</h1>
          <p className="text-muted-foreground text-sm">All runners across every squad.</p>
        </div>
        <div className="flex items-center gap-1 border border-border rounded overflow-hidden">
          {(["streak", "adherence", "name"] as SortKey[]).map((key) => (
            <button
              key={key}
              onClick={() => setSortBy(key)}
              className={cn(
                "px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors",
                sortBy === key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {key}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-px bg-border">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-card px-5 py-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/6" />
            </div>
          ))}
        </div>
      ) : sortedMembers.length === 0 ? (
        <div className="border border-dashed border-border rounded p-16 text-center">
          <p className="font-display text-xl font-bold mb-2">No runners yet.</p>
          <p className="text-muted-foreground text-sm">Add runners through a squad.</p>
          <Link href="/squads" className="text-muted-foreground text-sm mt-2 inline-block hover:text-foreground underline">
            View Squads
          </Link>
        </div>
      ) : (
        <div className="border border-border rounded overflow-hidden divide-y divide-border">
          {sortedMembers.map((member, i) => (
            <Link key={member.id} href={`/members/${member.id}`}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.03 }}
                className="px-5 py-4 flex items-center gap-4 hover:bg-accent/20 transition-colors cursor-pointer"
              >
                <span className="font-mono text-xs text-muted-foreground w-6 text-right flex-shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{member.name}</span>
                  </div>
                  <div className="text-muted-foreground text-xs font-mono">
                    {member.squadId === null
                      ? "No squad yet"
                      : (squadMap[member.squadId] ?? "Unknown squad")}
                    {member.goalRace && ` · ${member.goalRace}`}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="font-mono text-lg font-bold text-foreground">{member.currentStreak}</div>
                    <div className="text-muted-foreground text-xs font-mono uppercase">streak</div>
                  </div>
                  <div className="text-center">
                    <div className="font-mono text-lg font-bold">{member.longestStreak}</div>
                    <div className="text-muted-foreground text-xs font-mono uppercase">best</div>
                  </div>
                  <div className="w-24">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-mono text-muted-foreground">adherence</span>
                      <span className="text-xs font-mono font-bold">{member.adherencePct}%</span>
                    </div>
                    <div className="h-1 bg-secondary rounded-full overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full",
                          member.adherencePct >= 80 ? "bg-primary" : member.adherencePct >= 60 ? "bg-chart-2" : "bg-destructive/60"
                        )}
                        style={{ width: `${member.adherencePct}%` }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
